import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { SessionUser } from '@igs/shared';
import { fetchMe, logout as logoutApi } from './auth-api.js';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  readonly status: AuthStatus;
  readonly user: SessionUser | null;
  readonly refresh: () => Promise<void>;
  readonly logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export { AuthContext };

interface AuthProviderProps {
  readonly children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<SessionUser | null>(null);

  const refresh = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      const me = await fetchMe(signal);
      setUser(me.user);
      setStatus(me.authenticated ? 'authenticated' : 'unauthenticated');
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setUser(null);
      setStatus('unauthenticated');
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);

  const logout = useCallback(async (): Promise<void> => {
    await logoutApi();
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, refresh: () => refresh(), logout }),
    [status, user, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
